import { env } from "@/core/config/env";
import type {
  AutomationDelivery,
  AutomationReadiness,
  AutomationSettings,
  CatalogCategory,
  CatalogFileUpload,
  CatalogItem,
  CatalogVariant,
  CreateRetailDiscountCodeRequest,
  CreateRetailGiftCardRequest,
  CreateStaffMemberRequest,
  CustomerLanding,
  CustomerLoyalty,
  CustomerRetailHistory,
  PublicOrganizationProfile,
  PublicPersonalLanding,
  PublicRetailReceipt,
  ProductInteractionRecord,
  ProductInteractionSettings,
  ProductInteractionSummary,
  RecordProductInteractionRequest,
  ReservationNotificationSettings,
  RetailDashboard,
  RetailDiscountCode,
  RetailGiftCard,
  RetailInvoice,
  RetailLoyaltyProgram,
  RetailOneClickSaleRequest,
  RetailPaymentMethod,
  RetailProduct,
  RetailQuickInteractionRequest,
  RetailQuickInteractionResponse,
  RetailSaleSettings,
  SocialLink,
  StaffMemberResponse,
  TenantBranch,
  TenantDeliverySettings,
  TenantProfile,
  TenantPurchasePlan,
  TenantPurchaseRequest,
  TenantSubscription,
  UpdateRetailDiscountCodeRequest,
  UpdateRetailGiftCardRequest,
  UpdateReservationNotificationSettingsRequest,
  UpdateRetailLoyaltyProgramRequest,
  UpdateRetailSaleSettingsRequest,
  UpdateProductInteractionSettingsRequest,
  UpdateStaffMemberRequest,
  UpdateTenantDeliverySettingsRequest,
  UpdateTenantProfileRequest,
  UpsertCatalogCategoryRequest,
  UpsertCatalogItemRequest,
  UpsertCatalogStockRequest,
  UpsertCatalogVariantRequest,
  UpsertSocialLinkRequest,
  UpsertTenantBranchRequest,
} from "@/core/types/tenantWorkspace";
import { createUuid } from "@/core/utils/uuid";
import { httpClient } from "./httpClient";

const demoProfile: TenantProfile = {
  name: "همکاران راهکار فروش",
  tenantCode: "hamkar",
  description: "راهکارهای هوشمند فروش و ارتباط با مشتری",
  address: "تهران",
  logoUrl: null,
  bannerUrl: null,
  phone: "۰۲۱ ۴۴۸۸ ۲۳۴۵",
  email: "hello@hamkarcrm.ir",
  websiteUrl: "https://hamkarcrm.ir",
  catalogUrl: null,
  defaultCurrency: "IRR",
  socialLinks: [
    {
      id: createUuid(),
      platform: "instagram",
      title: "اینستاگرام",
      url: "https://instagram.com/hamkarcrm",
    },
  ],
  business: {
    typeCode: "Organization",
    typeName: "سازمانی",
    entityName: "سازمان",
    templateCode: "organization",
    templateName: "قالب سازمانی",
    templateDescription:
      "پیشنهاد شروع برای تیم فروش، سرنخ‌های B2B و پیگیری‌های چندمرحله‌ای.",
  },
};
const demoAutomation: AutomationSettings = {
  isEnabled: true,
  emailEnabled: true,
  smsEnabled: true,
  socialEnabled: false,
  messageSubject: "معرفی {{tenantName}}",
  messageTemplate:
    "سلام {{contactName}}، از آشنایی با شما خوشحالیم. برای مشاهده کاتالوگ محصولات، این لینک را ببینید: {{catalogLink}}",
};
const demoDeliverySettings: TenantDeliverySettings = {
  channelAccess: {
    emailAllowed: true,
    smsAllowed: false,
    socialAllowed: false,
  },
  email: {
    host: null,
    port: 587,
    useSsl: true,
    username: null,
    fromAddress: null,
    fromName: null,
    passwordConfigured: false,
    isConfigured: false,
  },
  social: { webhookUrl: null, apiKeyConfigured: false, isConfigured: false },
};
const demoReservationNotifications: ReservationNotificationSettings = {
  inAppEnabled: true,
  smsEnabled: false,
  smsRecipient: "09120000000",
  smsMessageTemplate:
    "درخواست رزرو جدید در {{storeName}}: {{customerName}} با شماره {{customerMobile}} برای {{itemName}} درخواست رزرو ثبت کرد. لطفاً موجودی و زمان نگهداری را بررسی کنید.",
  smsAllowed: true,
  canSendSmsNow: true,
  smsCreditUnlimited: false,
  smsCreditRemaining: 250,
};
let demoLoyaltyProgram: RetailLoyaltyProgram = {
  isEnabled: true,
  currency: "IRR",
  earningAmount: 100000,
  pointsPerEarning: 1,
  pointRedemptionValue: 1000,
  minimumPointsToRedeem: 10,
  silverSpendThreshold: 10000000,
  goldSpendThreshold: 50000000,
  platinumSpendThreshold: 150000000,
};
const demoDiscountCodes: RetailDiscountCode[] = [];
const demoGiftCards: RetailGiftCard[] = [];
const demoPurchaseRequests: TenantPurchaseRequest[] = [];
const demoRetailSaleSettings: RetailSaleSettings = {
  thankYouSmsEnabled: true,
  thankYouSmsTemplate:
    "{{customerName}} عزیز، از خرید شما از {{storeName}} متشکریم. فاکتور {{invoiceNumber}} به مبلغ {{totalAmount}} {{currency}} ثبت شد. جزئیات خرید: {{receiptLink}}",
  smsAllowed: true,
  canSendSmsNow: true,
  smsCreditUnlimited: true,
  smsCreditRemaining: null,
};
const demoProductInteractionSettings: ProductInteractionSettings = {
  options: [
    { key: "asked-price", title: "سؤال قیمت", campaignInterestType: "AskedPrice", isActive: true, sortOrder: 10 },
    { key: "tried", title: "پرو یا تست کرد", campaignInterestType: "Tried", isActive: true, sortOrder: 20 },
    { key: "interested", title: "علاقه‌مند شد", campaignInterestType: "Interested", isActive: true, sortOrder: 30 },
    { key: "reserved", title: "درخواست رزرو", campaignInterestType: "Reserved", isActive: true, sortOrder: 40 },
  ],
};
const demoRetailProducts: RetailProduct[] = [
  {
    id: "d1000000-0000-4000-8000-000000000001",
    name: "کتانی روزمره آریا",
    itemType: "Product",
    sku: "SH-101",
    barcode: "6260000001012",
    price: 42500000,
    previousPrice: 48000000,
    currency: "IRR",
    imageUrl: null,
    brand: "آریا",
    availabilityStatus: "Available",
    isFavorite: true,
    variants: [
      {
        id: "d2000000-0000-4000-8000-000000000001",
        name: "مشکی / ۴۱",
        color: "مشکی",
        size: "۴۱",
        sku: "SH-101-B-41",
        barcode: null,
        priceOverride: null,
        totalStock: 6,
        stocks: [
          {
            branchId: "d3000000-0000-4000-8000-000000000001",
            branchName: "شعبه مرکزی",
            branchCode: "MAIN",
            onHandQuantity: 6,
            reorderPoint: 2,
            isLowStock: false,
          },
        ],
      },
      {
        id: "d2000000-0000-4000-8000-000000000002",
        name: "سفید / ۴۲",
        color: "سفید",
        size: "۴۲",
        sku: "SH-101-W-42",
        barcode: null,
        priceOverride: 44000000,
        totalStock: 2,
        stocks: [
          {
            branchId: "d3000000-0000-4000-8000-000000000001",
            branchName: "شعبه مرکزی",
            branchCode: "MAIN",
            onHandQuantity: 2,
            reorderPoint: 2,
            isLowStock: true,
          },
        ],
      },
    ],
  },
  {
    id: "d1000000-0000-4000-8000-000000000002",
    name: "کیف چرمی کلاسیک",
    itemType: "Product",
    sku: "BG-204",
    barcode: "6260000002040",
    price: 63500000,
    previousPrice: null,
    currency: "IRR",
    imageUrl: null,
    brand: "چرم پارس",
    availabilityStatus: "Available",
    isFavorite: false,
    variants: [],
  },
  {
    id: "d1000000-0000-4000-8000-000000000003",
    name: "خدمات بسته‌بندی ویژه",
    itemType: "Service",
    sku: "SRV-GIFT",
    barcode: null,
    price: 2500000,
    previousPrice: null,
    currency: "IRR",
    imageUrl: null,
    brand: null,
    availabilityStatus: "Available",
    isFavorite: false,
    variants: [],
  },
];

export const tenantWorkspaceApi = {
  async subscription(): Promise<TenantSubscription> {
    if (env.isDemo)
      return {
        planName: "Professional",
        startsAtUtc: new Date().toISOString(),
        endsAtUtc: new Date(Date.now() + 31536000000).toISOString(),
        maxCustomerAccounts: 5000,
        maxStaff: 25,
        maxLeads: 0,
        maxCatalogItems: 0,
        smsEnabled: false,
        smsCreditEnabled: false,
        smsCreditLimit: 0,
        smsCreditUsed: 0,
        smsCreditRemaining: null,
        smsCreditUnlimited: false,
        isActive: true,
        planId: null,
      };
    return (
      await httpClient.get<TenantSubscription>(
        "/api/v1/tenant-workspace/subscription",
      )
    ).data;
  },
  async purchasePlans(): Promise<TenantPurchasePlan[]> {
    if (env.isDemo)
      return [
        {
          id: "e7000000-0000-4000-8000-000000000001",
          name: "رشد فروشگاهی",
          maxCustomerAccounts: 5000,
          maxStaff: 25,
          maxLeads: 0,
          maxCatalogItems: 1000,
          smsEnabled: true,
          smsCreditEnabled: true,
          smsCreditLimit: 500,
          emailEnabled: true,
          socialEnabled: true,
          features: [
            { code: "CUSTOMERS", title: "مخاطبان و مشتریان" },
            { code: "PRODUCTS", title: "محصولات و خدمات" },
            { code: "SMS", title: "ارسال پیامک" },
          ],
        },
      ];
    return (
      await httpClient.get<TenantPurchasePlan[]>(
        "/api/v1/tenant-workspace/billing/plans",
      )
    ).data;
  },
  async purchaseRequests(): Promise<TenantPurchaseRequest[]> {
    if (env.isDemo) return [...demoPurchaseRequests];
    return (
      await httpClient.get<TenantPurchaseRequest[]>(
        "/api/v1/tenant-workspace/billing/requests",
      )
    ).data;
  },
  async requestPlanPurchase(payload: {
    planId: string;
    durationMonths: number;
    note?: string | null;
  }): Promise<TenantPurchaseRequest> {
    if (env.isDemo) {
      const plan = (await this.purchasePlans()).find((item) => item.id === payload.planId);
      const request: TenantPurchaseRequest = {
        id: createUuid(), tenantId: "demo", tenantName: null, tenantCode: null,
        requestType: "Plan", status: "Pending", planId: payload.planId,
        planName: plan?.name ?? "پلن انتخابی", durationMonths: payload.durationMonths,
        smsCreditAmount: 0, requestNote: payload.note ?? null,
        requestedAtUtc: new Date().toISOString(), decidedAtUtc: null,
        decidedByDisplayName: null, decisionNote: null, requestedByMembershipId: null,
      };
      demoPurchaseRequests.unshift(request);
      return request;
    }
    return (
      await httpClient.post<TenantPurchaseRequest>(
        "/api/v1/tenant-workspace/billing/plan-requests",
        payload,
      )
    ).data;
  },
  async requestSmsCreditPurchase(payload: {
    amount: number;
    note?: string | null;
  }): Promise<TenantPurchaseRequest> {
    if (env.isDemo) {
      const request: TenantPurchaseRequest = {
        id: createUuid(), tenantId: "demo", tenantName: null, tenantCode: null,
        requestType: "SmsCredit", status: "Pending", planId: null, planName: null,
        durationMonths: 0, smsCreditAmount: payload.amount,
        requestNote: payload.note ?? null, requestedAtUtc: new Date().toISOString(),
        decidedAtUtc: null, decidedByDisplayName: null, decisionNote: null,
        requestedByMembershipId: null,
      };
      demoPurchaseRequests.unshift(request);
      return request;
    }
    return (
      await httpClient.post<TenantPurchaseRequest>(
        "/api/v1/tenant-workspace/billing/sms-credit-requests",
        payload,
      )
    ).data;
  },
  async cancelPurchaseRequest(requestId: string): Promise<TenantPurchaseRequest> {
    if (env.isDemo) {
      const request = demoPurchaseRequests.find((item) => item.id === requestId);
      if (!request) throw new Error("درخواست پیدا نشد.");
      request.status = "Cancelled";
      request.decidedAtUtc = new Date().toISOString();
      request.decisionNote = "لغو توسط مدیر شرکت";
      return request;
    }
    return (
      await httpClient.post<TenantPurchaseRequest>(
        `/api/v1/tenant-workspace/billing/requests/${requestId}/cancel`,
        {},
      )
    ).data;
  },
  async profile(): Promise<TenantProfile> {
    if (env.isDemo) return demoProfile;
    return (
      await httpClient.get<TenantProfile>("/api/v1/tenant-workspace/profile")
    ).data;
  },
  async updateProfile(
    request: UpdateTenantProfileRequest,
  ): Promise<TenantProfile> {
    if (env.isDemo)
      return {
        ...demoProfile,
        ...request,
        defaultCurrency: request.defaultCurrency ?? demoProfile.defaultCurrency,
      };
    return (
      await httpClient.request<TenantProfile>(
        "/api/v1/tenant-workspace/profile",
        { method: "PUT", body: request },
      )
    ).data;
  },
  async uploadProfileBanner(file: File): Promise<CatalogFileUpload> {
    const body = new FormData();
    body.append("file", file);
    return (
      await httpClient.request<CatalogFileUpload>(
        "/api/v1/tenant-workspace/profile/banner",
        { method: "POST", body, timeoutMs: 60_000 },
      )
    ).data;
  },
  async catalog(): Promise<CatalogItem[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<CatalogItem[]>("/api/v1/tenant-workspace/catalog")
    ).data;
  },
  async catalogCategories(): Promise<CatalogCategory[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<CatalogCategory[]>(
        "/api/v1/tenant-workspace/catalog/categories",
      )
    ).data;
  },
  async createCatalogCategory(
    request: UpsertCatalogCategoryRequest,
  ): Promise<CatalogCategory> {
    if (env.isDemo)
      return {
        id: createUuid(),
        name: request.name,
        description: request.description ?? null,
        parentId: request.parentId ?? null,
        sortOrder: request.sortOrder ?? 0,
        isActive: request.isActive,
        itemCount: 0,
      };
    return (
      await httpClient.post<CatalogCategory>(
        "/api/v1/tenant-workspace/catalog/categories",
        request,
      )
    ).data;
  },
  async updateCatalogCategory(
    id: string,
    request: UpsertCatalogCategoryRequest,
  ): Promise<CatalogCategory> {
    if (env.isDemo)
      return {
        id,
        name: request.name,
        description: request.description ?? null,
        parentId: request.parentId ?? null,
        sortOrder: request.sortOrder ?? 0,
        isActive: request.isActive,
        itemCount: 0,
      };
    return (
      await httpClient.request<CatalogCategory>(
        `/api/v1/tenant-workspace/catalog/categories/${id}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async deleteCatalogCategory(id: string): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(
      `/api/v1/tenant-workspace/catalog/categories/${id}`,
      { method: "DELETE" },
    );
  },
  async createCatalogItem(
    request: UpsertCatalogItemRequest,
  ): Promise<CatalogItem> {
    if (env.isDemo)
      return {
        id: createUuid(),
        name: request.name,
        description: request.description ?? null,
        catalogUrl: request.catalogUrl ?? null,
        price: request.price ?? null,
        previousPrice: request.previousPrice ?? null,
        currency: request.currency ?? "IRR",
        isPublished: request.isPublished,
        categoryId: request.categoryId ?? null,
        categoryName: null,
        itemType: request.itemType ?? "Product",
        code: request.code ?? null,
        barcode: request.barcode ?? null,
        brand: request.brand ?? null,
        unit: request.unit ?? "عدد",
        availabilityStatus: request.availabilityStatus ?? "Available",
        isFeatured: request.isFeatured ?? false,
        sortOrder: request.sortOrder ?? 0,
      };
    return (
      await httpClient.post<CatalogItem>(
        "/api/v1/tenant-workspace/catalog",
        request,
      )
    ).data;
  },
  async uploadCatalogFile(file: File): Promise<CatalogFileUpload> {
    const body = new FormData();
    body.append("file", file);
    return (
      await httpClient.request<CatalogFileUpload>(
        "/api/v1/tenant-workspace/catalog/upload",
        {
          method: "POST",
          body,
          timeoutMs: 60_000,
        },
      )
    ).data;
  },
  async updateCatalogItem(
    id: string,
    request: UpsertCatalogItemRequest,
  ): Promise<CatalogItem> {
    if (env.isDemo)
      return {
        id,
        name: request.name,
        description: request.description ?? null,
        catalogUrl: request.catalogUrl ?? null,
        price: request.price ?? null,
        previousPrice: request.previousPrice ?? null,
        currency: request.currency ?? "IRR",
        isPublished: request.isPublished,
        categoryId: request.categoryId ?? null,
        categoryName: null,
        itemType: request.itemType ?? "Product",
        code: request.code ?? null,
        barcode: request.barcode ?? null,
        brand: request.brand ?? null,
        unit: request.unit ?? "عدد",
        availabilityStatus: request.availabilityStatus ?? "Available",
        isFeatured: request.isFeatured ?? false,
        sortOrder: request.sortOrder ?? 0,
      };
    return (
      await httpClient.request<CatalogItem>(
        `/api/v1/tenant-workspace/catalog/${id}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async deleteCatalogItem(id: string): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(`/api/v1/tenant-workspace/catalog/${id}`, {
      method: "DELETE",
    });
  },
  async branches(): Promise<TenantBranch[]> {
    if (env.isDemo)
      return [
        {
          id: "d3000000-0000-4000-8000-000000000001",
          name: "شعبه مرکزی",
          code: "MAIN",
          address: "تهران",
          phone: null,
          isActive: true,
        },
      ];
    return (
      await httpClient.get<TenantBranch[]>("/api/v1/tenant-workspace/branches")
    ).data;
  },
  async createBranch(
    request: UpsertTenantBranchRequest,
  ): Promise<TenantBranch> {
    if (env.isDemo)
      return {
        id: createUuid(),
        name: request.name,
        code: request.code.toUpperCase(),
        address: request.address ?? null,
        phone: request.phone ?? null,
        isActive: request.isActive,
      };
    return (
      await httpClient.post<TenantBranch>(
        "/api/v1/tenant-workspace/branches",
        request,
      )
    ).data;
  },
  async updateBranch(
    id: string,
    request: UpsertTenantBranchRequest,
  ): Promise<TenantBranch> {
    if (env.isDemo)
      return {
        id,
        name: request.name,
        code: request.code.toUpperCase(),
        address: request.address ?? null,
        phone: request.phone ?? null,
        isActive: request.isActive,
      };
    return (
      await httpClient.request<TenantBranch>(
        `/api/v1/tenant-workspace/branches/${id}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async deleteBranch(id: string): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(`/api/v1/tenant-workspace/branches/${id}`, {
      method: "DELETE",
    });
  },
  async catalogVariants(catalogItemId: string): Promise<CatalogVariant[]> {
    if (env.isDemo)
      return (
        demoRetailProducts
          .find((product) => product.id === catalogItemId)
          ?.variants.map((variant) => ({
            ...variant,
            catalogItemId,
            isActive: true,
            sortOrder: 0,
            stocks: variant.stocks.map((stock, index) => ({
              id: `${variant.id}-${index}`,
              ...stock,
              branchIsActive: true,
            })),
          })) ?? []
      );
    return (
      await httpClient.get<CatalogVariant[]>(
        `/api/v1/tenant-workspace/catalog/${catalogItemId}/variants`,
      )
    ).data;
  },
  async createCatalogVariant(
    catalogItemId: string,
    request: UpsertCatalogVariantRequest,
  ): Promise<CatalogVariant> {
    if (env.isDemo)
      return {
        id: createUuid(),
        catalogItemId,
        name:
          request.name ||
          [request.color, request.size].filter(Boolean).join(" / "),
        color: request.color ?? null,
        size: request.size ?? null,
        sku: request.sku ?? null,
        barcode: request.barcode ?? null,
        priceOverride: request.priceOverride ?? null,
        isActive: request.isActive,
        sortOrder: request.sortOrder ?? 0,
        stocks: [],
        totalStock: 0,
      };
    return (
      await httpClient.post<CatalogVariant>(
        `/api/v1/tenant-workspace/catalog/${catalogItemId}/variants`,
        request,
      )
    ).data;
  },
  async updateCatalogVariant(
    catalogItemId: string,
    variantId: string,
    request: UpsertCatalogVariantRequest,
  ): Promise<CatalogVariant> {
    if (env.isDemo)
      return {
        id: variantId,
        catalogItemId,
        name:
          request.name ||
          [request.color, request.size].filter(Boolean).join(" / "),
        color: request.color ?? null,
        size: request.size ?? null,
        sku: request.sku ?? null,
        barcode: request.barcode ?? null,
        priceOverride: request.priceOverride ?? null,
        isActive: request.isActive,
        sortOrder: request.sortOrder ?? 0,
        stocks: [],
        totalStock: 0,
      };
    return (
      await httpClient.request<CatalogVariant>(
        `/api/v1/tenant-workspace/catalog/${catalogItemId}/variants/${variantId}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async deleteCatalogVariant(
    catalogItemId: string,
    variantId: string,
  ): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(
      `/api/v1/tenant-workspace/catalog/${catalogItemId}/variants/${variantId}`,
      { method: "DELETE" },
    );
  },
  async updateCatalogStock(
    variantId: string,
    request: UpsertCatalogStockRequest,
  ): Promise<CatalogVariant> {
    if (env.isDemo) throw new Error("ویرایش موجودی در حالت دمو ذخیره نمی‌شود.");
    return (
      await httpClient.request<CatalogVariant>(
        `/api/v1/tenant-workspace/catalog/variants/${variantId}/stock`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async socialLinks(): Promise<SocialLink[]> {
    if (env.isDemo) return demoProfile.socialLinks;
    return (
      await httpClient.get<SocialLink[]>(
        "/api/v1/tenant-workspace/social-links",
      )
    ).data;
  },
  async createSocialLink(
    request: UpsertSocialLinkRequest,
  ): Promise<SocialLink> {
    if (env.isDemo) return { id: createUuid(), ...request };
    return (
      await httpClient.post<SocialLink>(
        "/api/v1/tenant-workspace/social-links",
        request,
      )
    ).data;
  },
  async updateSocialLink(
    id: string,
    request: UpsertSocialLinkRequest,
  ): Promise<SocialLink> {
    if (env.isDemo) return { id, ...request };
    return (
      await httpClient.request<SocialLink>(
        `/api/v1/tenant-workspace/social-links/${id}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async deleteSocialLink(id: string): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(
      `/api/v1/tenant-workspace/social-links/${id}`,
      { method: "DELETE" },
    );
  },
  async automation(): Promise<AutomationSettings> {
    if (env.isDemo) return demoAutomation;
    return (
      await httpClient.get<AutomationSettings>(
        "/api/v1/tenant-workspace/automation",
      )
    ).data;
  },
  async updateAutomation(
    request: AutomationSettings,
  ): Promise<AutomationSettings> {
    if (env.isDemo) return { ...request };
    return (
      await httpClient.request<AutomationSettings>(
        "/api/v1/tenant-workspace/automation",
        { method: "PUT", body: request },
      )
    ).data;
  },
  async deliverySettings(): Promise<TenantDeliverySettings> {
    if (env.isDemo) return demoDeliverySettings;
    return (
      await httpClient.get<TenantDeliverySettings>(
        "/api/v1/tenant-workspace/delivery-settings",
      )
    ).data;
  },
  async updateDeliverySettings(
    request: UpdateTenantDeliverySettingsRequest,
  ): Promise<TenantDeliverySettings> {
    if (env.isDemo) {
      return {
        ...demoDeliverySettings,
        email: {
          ...demoDeliverySettings.email,
          host: request.emailHost,
          port: request.emailPort,
          useSsl: request.emailUseSsl,
          username: request.emailUsername,
          fromAddress: request.emailFromAddress,
          fromName: request.emailFromName,
          passwordConfigured:
            Boolean(request.emailPassword) ||
            (demoDeliverySettings.email.passwordConfigured &&
              !request.clearEmailPassword),
          isConfigured: Boolean(request.emailHost && request.emailFromAddress),
        },
        social: {
          webhookUrl: request.socialWebhookUrl,
          apiKeyConfigured:
            Boolean(request.socialApiKey) ||
            (demoDeliverySettings.social.apiKeyConfigured &&
              !request.clearSocialApiKey),
          isConfigured: Boolean(request.socialWebhookUrl),
        },
      };
    }
    return (
      await httpClient.request<TenantDeliverySettings>(
        "/api/v1/tenant-workspace/delivery-settings",
        { method: "PUT", body: request },
      )
    ).data;
  },
  async reservationNotifications(): Promise<ReservationNotificationSettings> {
    if (env.isDemo) return demoReservationNotifications;
    return (
      await httpClient.get<ReservationNotificationSettings>(
        "/api/v1/tenant-workspace/reservation-notifications",
      )
    ).data;
  },
  async updateReservationNotifications(
    request: UpdateReservationNotificationSettingsRequest,
  ): Promise<ReservationNotificationSettings> {
    if (env.isDemo) return { ...demoReservationNotifications, ...request };
    return (
      await httpClient.request<ReservationNotificationSettings>(
        "/api/v1/tenant-workspace/reservation-notifications",
        { method: "PUT", body: request },
      )
    ).data;
  },
  async retailSaleSettings(): Promise<RetailSaleSettings> {
    if (env.isDemo) return demoRetailSaleSettings;
    return (
      await httpClient.get<RetailSaleSettings>(
        "/api/v1/tenant-workspace/retail-sales-settings",
      )
    ).data;
  },
  async updateRetailSaleSettings(
    request: UpdateRetailSaleSettingsRequest,
  ): Promise<RetailSaleSettings> {
    if (env.isDemo) return { ...demoRetailSaleSettings, ...request };
    return (
      await httpClient.request<RetailSaleSettings>(
        "/api/v1/tenant-workspace/retail-sales-settings",
        { method: "PUT", body: request },
      )
    ).data;
  },
  async productInteractionSettings(): Promise<ProductInteractionSettings> {
    if (env.isDemo) return demoProductInteractionSettings;
    return (await httpClient.get<ProductInteractionSettings>("/api/v1/tenant-workspace/product-interaction-settings")).data;
  },
  async updateProductInteractionSettings(
    request: UpdateProductInteractionSettingsRequest,
  ): Promise<ProductInteractionSettings> {
    if (env.isDemo) {
      demoProductInteractionSettings.options = request.options.map((option, index) => ({ ...option, sortOrder: index + 1 }));
      return demoProductInteractionSettings;
    }
    return (await httpClient.request<ProductInteractionSettings>("/api/v1/tenant-workspace/product-interaction-settings", { method: "PUT", body: request })).data;
  },
  async automationDeliveries(): Promise<AutomationDelivery[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<AutomationDelivery[]>(
        "/api/v1/automation/deliveries?take=30",
      )
    ).data;
  },
  async automationReadiness(): Promise<AutomationReadiness> {
    if (env.isDemo)
      return {
        emailConfigured: true,
        smsConfigured: false,
        socialConfigured: false,
      };
    return (
      await httpClient.get<AutomationReadiness>("/api/v1/automation/readiness")
    ).data;
  },
  async createStaff(
    request: CreateStaffMemberRequest,
  ): Promise<StaffMemberResponse> {
    if (env.isDemo)
      return {
        membershipId: createUuid(),
        userId: createUuid(),
        username: request.username,
        displayName: request.displayName,
        jobTitle: request.jobTitle ?? null,
        status: "Active",
        email: request.email,
        mobile: request.mobile ?? null,
        branchId: null,
        isTenantOwner: false,
      };
    return (
      await httpClient.post<StaffMemberResponse>("/api/v1/staff", request)
    ).data;
  },
  async updateStaff(
    membershipId: string,
    request: UpdateStaffMemberRequest,
  ): Promise<StaffMemberResponse> {
    if (env.isDemo)
      return {
        membershipId,
        userId: membershipId,
        username: request.username,
        displayName: request.displayName,
        jobTitle: request.jobTitle ?? null,
        status: request.status ?? "Active",
        email: null,
        mobile: null,
        branchId: request.branchId ?? null,
        isTenantOwner: false,
      };
    return (
      await httpClient.patch<StaffMemberResponse>(
        `/api/v1/staff/${membershipId}`,
        request,
      )
    ).data;
  },
  async publicProfile(tenantCode: string): Promise<PublicOrganizationProfile> {
    return (
      await httpClient.get<PublicOrganizationProfile>(
        `/api/v1/public/organizations/${encodeURIComponent(tenantCode)}`,
        { authenticated: false, retryOnUnauthorized: false },
      )
    ).data;
  },
  async retailProducts(query = ""): Promise<RetailProduct[]> {
    if (env.isDemo) {
      const normalized = query.trim().toLocaleLowerCase("fa-IR");
      return normalized
        ? demoRetailProducts.filter((item) =>
            `${item.name} ${item.sku ?? ""} ${item.barcode ?? ""} ${item.brand ?? ""}`
              .toLocaleLowerCase("fa-IR")
              .includes(normalized),
          )
        : demoRetailProducts;
    }
    return (
      await httpClient.get<{ items: RetailProduct[] }>(
        `/api/v1/retail/products/search?query=${encodeURIComponent(query)}&take=40`,
      )
    ).data.items;
  },
  async retailRecent(): Promise<RetailProduct[]> {
    if (env.isDemo) return demoRetailProducts;
    return (
      await httpClient.get<RetailProduct[]>("/api/v1/retail/products/recent")
    ).data;
  },
  async setRetailFavorite(
    productId: string,
    isFavorite: boolean,
  ): Promise<void> {
    if (env.isDemo) return;
    await httpClient.request<void>(
      `/api/v1/retail/products/${productId}/favorite`,
      { method: "PUT", body: { isFavorite } },
    );
  },
  async retailLoyaltyProgram(): Promise<RetailLoyaltyProgram> {
    if (env.isDemo) return demoLoyaltyProgram;
    return (
      await httpClient.get<RetailLoyaltyProgram>(
        "/api/v1/retail/loyalty/program",
      )
    ).data;
  },
  async updateRetailLoyaltyProgram(
    request: UpdateRetailLoyaltyProgramRequest,
  ): Promise<RetailLoyaltyProgram> {
    if (env.isDemo) {
      demoLoyaltyProgram = { ...request };
      return demoLoyaltyProgram;
    }
    return (
      await httpClient.request<RetailLoyaltyProgram>(
        "/api/v1/retail/loyalty/program",
        { method: "PUT", body: request },
      )
    ).data;
  },
  async customerLoyalty(customerId: string): Promise<CustomerLoyalty> {
    if (env.isDemo)
      return {
        customerId,
        pointsBalance: 0,
        lifetimeEarnedPoints: 0,
        lifetimeRedeemedPoints: 0,
        lifetimeSpend: 0,
        vipLevel: "Standard",
        currency: demoLoyaltyProgram.currency,
        transactions: [],
      };
    return (
      await httpClient.get<CustomerLoyalty>(
        `/api/v1/retail/loyalty/customers/${encodeURIComponent(customerId)}`,
      )
    ).data;
  },
  async adjustCustomerLoyaltyPoints(
    customerId: string,
    request: { pointsDelta: number; note: string },
  ): Promise<CustomerLoyalty> {
    if (env.isDemo) {
      const current = await this.customerLoyalty(customerId);
      return {
        ...current,
        pointsBalance: Math.max(0, current.pointsBalance + request.pointsDelta),
      };
    }
    return (
      await httpClient.post<CustomerLoyalty>(
        `/api/v1/retail/loyalty/customers/${encodeURIComponent(customerId)}/points-adjustments`,
        request,
      )
    ).data;
  },
  async retailDiscountCodes(): Promise<RetailDiscountCode[]> {
    if (env.isDemo) return demoDiscountCodes;
    return (
      await httpClient.get<RetailDiscountCode[]>(
        "/api/v1/retail/loyalty/discount-codes",
      )
    ).data;
  },
  async createRetailDiscountCode(
    request: CreateRetailDiscountCodeRequest,
  ): Promise<RetailDiscountCode> {
    if (env.isDemo) {
      const item: RetailDiscountCode = {
        id: createUuid(),
        ...request,
        customerId: request.customerId ?? null,
        maximumDiscountAmount: request.maximumDiscountAmount ?? null,
        startsAtUtc: request.startsAtUtc ?? null,
        expiresAtUtc: request.expiresAtUtc ?? null,
        maximumRedemptions: request.maximumRedemptions ?? null,
        redemptionCount: 0,
        isAvailable: request.isActive,
      };
      demoDiscountCodes.unshift(item);
      return item;
    }
    return (
      await httpClient.post<RetailDiscountCode>(
        "/api/v1/retail/loyalty/discount-codes",
        request,
      )
    ).data;
  },
  async updateRetailDiscountCode(
    id: string,
    request: UpdateRetailDiscountCodeRequest,
  ): Promise<RetailDiscountCode> {
    if (env.isDemo) {
      const index = demoDiscountCodes.findIndex((item) => item.id === id);
      if (index < 0) throw new Error("کد تخفیف پیدا نشد.");
      demoDiscountCodes[index] = {
        ...demoDiscountCodes[index],
        ...request,
        customerId: request.customerId ?? null,
        maximumDiscountAmount: request.maximumDiscountAmount ?? null,
        startsAtUtc: request.startsAtUtc ?? null,
        expiresAtUtc: request.expiresAtUtc ?? null,
        maximumRedemptions: request.maximumRedemptions ?? null,
        isAvailable: request.isActive,
      };
      return demoDiscountCodes[index];
    }
    return (
      await httpClient.request<RetailDiscountCode>(
        `/api/v1/retail/loyalty/discount-codes/${encodeURIComponent(id)}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async retailGiftCards(): Promise<RetailGiftCard[]> {
    if (env.isDemo) return demoGiftCards;
    return (
      await httpClient.get<RetailGiftCard[]>(
        "/api/v1/retail/loyalty/gift-cards",
      )
    ).data;
  },
  async createRetailGiftCard(
    request: CreateRetailGiftCardRequest,
  ): Promise<RetailGiftCard> {
    if (env.isDemo) {
      const item: RetailGiftCard = {
        id: createUuid(),
        code: request.code,
        customerId: request.customerId ?? null,
        initialBalance: request.initialBalance,
        balance: request.initialBalance,
        currency: request.currency,
        expiresAtUtc: request.expiresAtUtc ?? null,
        isActive: request.isActive,
        isAvailable: request.isActive,
      };
      demoGiftCards.unshift(item);
      return item;
    }
    return (
      await httpClient.post<RetailGiftCard>(
        "/api/v1/retail/loyalty/gift-cards",
        request,
      )
    ).data;
  },
  async updateRetailGiftCard(
    id: string,
    request: UpdateRetailGiftCardRequest,
  ): Promise<RetailGiftCard> {
    if (env.isDemo) {
      const index = demoGiftCards.findIndex((item) => item.id === id);
      if (index < 0) throw new Error("کارت هدیه پیدا نشد.");
      demoGiftCards[index] = {
        ...demoGiftCards[index],
        ...request,
        customerId: request.customerId ?? null,
        expiresAtUtc: request.expiresAtUtc ?? null,
        isAvailable: request.isActive && demoGiftCards[index].balance > 0,
      };
      return demoGiftCards[index];
    }
    return (
      await httpClient.request<RetailGiftCard>(
        `/api/v1/retail/loyalty/gift-cards/${encodeURIComponent(id)}`,
        { method: "PUT", body: request },
      )
    ).data;
  },
  async retailDashboard(): Promise<RetailDashboard> {
    if (env.isDemo)
      return {
        todayCustomers: 0,
        todayProductViews: 0,
        todayLandings: 0,
        todayOpened: 0,
        todayPurchases: 0,
        monthFollowUpPurchases: 0,
        returningCustomers: 0,
        popularProducts: [],
      };
    return (await httpClient.get<RetailDashboard>("/api/v1/retail/dashboard"))
      .data;
  },
  async createRetailQuickInteraction(
    request: RetailQuickInteractionRequest,
  ): Promise<RetailQuickInteractionResponse> {
    if (env.isDemo) {
      const productIds = Array.from(
        new Set(
          [request.productId, ...(request.productIds ?? [])].filter(
            (id): id is string => Boolean(id),
          ),
        ),
      );
      return {
        customerId: createUuid(),
        customerName: request.customerName || request.mobile,
        customerCreated: true,
        productId: productIds[0] ?? "",
        productIds,
        items: [],
        landingUrl: `${window.location.origin}/l/demo`,
        deliveryStatus: "Sent",
        deliveryError: null,
      };
    }
    return (
      await httpClient.post<RetailQuickInteractionResponse>(
        "/api/v1/retail/quick-interactions",
        request,
      )
    ).data;
  },
  async recordProductInteraction(
    request: RecordProductInteractionRequest,
  ): Promise<ProductInteractionRecord> {
    if (env.isDemo) {
      const product = demoRetailProducts.find((item) => item.id === request.productId);
      const option = demoProductInteractionSettings.options.find((item) => item.key === request.interactionKey);
      return {
        id: createUuid(), productId: request.productId, productName: product?.name ?? "کالا یا خدمت",
        interactionKey: request.interactionKey, interactionTitle: option?.title ?? request.interactionKey,
        customerId: request.mobile ? createUuid() : null, occurredAtUtc: new Date().toISOString(),
      };
    }
    return (await httpClient.post<ProductInteractionRecord>("/api/v1/retail/product-interactions", request)).data;
  },
  async productInteractionSummary(): Promise<ProductInteractionSummary> {
    if (env.isDemo) return { todayCount: 0, sinceUtc: new Date().toISOString(), topProducts: [] };
    return (await httpClient.get<ProductInteractionSummary>("/api/v1/retail/product-interactions/summary")).data;
  },
  async createRetailOneClickSale(
    request: RetailOneClickSaleRequest,
    idempotencyKey: string,
  ): Promise<RetailInvoice> {
    if (env.isDemo) {
      const now = new Date().toISOString();
      const id = createUuid();
      const items = request.items.map((line, index) => {
        const product = demoRetailProducts.find(
          (item) => item.id === line.catalogItemId,
        )!;
        const unitPrice = product.price ?? 0;
        return {
          catalogItemId: product.id,
          itemType: product.itemType,
          name: product.name,
          sku: product.sku,
          quantity: line.quantity,
          unitPrice,
          lineTotal: unitPrice * line.quantity,
          sortOrder: index,
        };
      });
      const subtotal = items.reduce((sum, item) => sum + item.lineTotal, 0);
      const totalAmount = Math.max(0, subtotal - request.discountAmount);
      const paidAmount =
        request.paymentStatus === "Unpaid"
          ? 0
          : request.paymentStatus === "PartiallyPaid"
            ? Math.max(0, Math.min(request.paidAmount ?? 0, totalAmount))
            : totalAmount;
      const paymentStatus =
        paidAmount === totalAmount
          ? "Paid"
          : paidAmount > 0
            ? "PartiallyPaid"
            : "Unpaid";
      return {
        invoiceId: id,
        invoiceNumber: `FS-${Date.now().toString().slice(-8)}`,
        customerId: createUuid(),
        customerName: request.customerName || request.mobile || "مشتری حضوری",
        customerMobile: request.mobile || "",
        paymentMethod: request.paymentMethod,
        paymentStatus,
        currency: "IRR",
        subtotal,
        discountAmount: request.discountAmount,
        totalAmount,
        paidAmount,
        refundedAmount: 0,
        netAmount: totalAmount,
        balanceDue: totalAmount - paidAmount,
        status: "Completed",
        thankYouDeliveryStatus: request.mobile ? "sent" : "Skipped",
        thankYouDeliveryError: null,
        createdAtUtc: now,
        receiptUrl: `${window.location.origin}/receipt/demo`,
        isReplay: false,
        items,
        payments: [],
        returns: [],
      };
    }
    return (
      await httpClient.post<RetailInvoice>(
        "/api/v1/retail/sales/one-click",
        request,
        { headers: { "Idempotency-Key": idempotencyKey } },
      )
    ).data;
  },
  async retailInvoices(take = 20): Promise<RetailInvoice[]> {
    if (env.isDemo) return [];
    return (
      await httpClient.get<RetailInvoice[]>(`/api/v1/retail/sales?take=${take}`)
    ).data;
  },
  async addRetailInvoicePayment(
    invoiceId: string,
    request: {
      paymentMethod: RetailPaymentMethod;
      amount: number;
      reference?: string | null;
    },
  ): Promise<RetailInvoice> {
    if (env.isDemo) throw new Error("ثبت پرداخت در نسخه نمایشی ذخیره نمی‌شود.");
    return (
      await httpClient.post<RetailInvoice>(
        `/api/v1/retail/sales/${encodeURIComponent(invoiceId)}/payments`,
        request,
      )
    ).data;
  },
  async returnRetailInvoice(
    invoiceId: string,
    request: {
      items: Array<{ invoiceItemId: string; quantity: number }>;
      refundMethod: RetailPaymentMethod;
      reason?: string | null;
    },
  ): Promise<RetailInvoice> {
    if (env.isDemo) throw new Error("ثبت مرجوعی در نسخه نمایشی ذخیره نمی‌شود.");
    return (
      await httpClient.post<RetailInvoice>(
        `/api/v1/retail/sales/${encodeURIComponent(invoiceId)}/returns`,
        request,
      )
    ).data;
  },
  async cancelRetailInvoice(invoiceId: string): Promise<RetailInvoice> {
    if (env.isDemo) throw new Error("لغو سفارش در نسخه نمایشی ذخیره نمی‌شود.");
    return (
      await httpClient.post<RetailInvoice>(
        `/api/v1/retail/sales/${encodeURIComponent(invoiceId)}/cancel`,
        {},
      )
    ).data;
  },
  async publicRetailReceipt(token: string): Promise<PublicRetailReceipt> {
    if (env.isDemo)
      return {
        invoiceNumber: "FS-DEMO",
        storeName: "فروشگاه نمونه",
        customerName: "مشتری نمونه",
        customerMobile: "0912***4567",
        paymentMethod: "Card",
        paymentStatus: "Paid",
        currency: "IRR",
        subtotal: 0,
        discountAmount: 0,
        totalAmount: 0,
        paidAmount: 0,
        refundedAmount: 0,
        netAmount: 0,
        balanceDue: 0,
        status: "Completed",
        createdAtUtc: new Date().toISOString(),
        items: [],
      };
    return (
      await httpClient.get<PublicRetailReceipt>(
        `/api/v1/public/retail/receipts/${encodeURIComponent(token)}`,
        { authenticated: false, retryOnUnauthorized: false },
      )
    ).data;
  },
  async customerRetailHistory(
    customerId: string,
  ): Promise<CustomerRetailHistory> {
    if (env.isDemo)
      return { customerId, interests: [], landings: [], purchases: [] };
    return (
      await httpClient.get<CustomerRetailHistory>(
        `/api/v1/retail/customers/${encodeURIComponent(customerId)}/history`,
      )
    ).data;
  },
  async revokeCustomerLanding(landingId: string): Promise<CustomerLanding> {
    return (
      await httpClient.post<CustomerLanding>(
        `/api/v1/retail/landings/${encodeURIComponent(landingId)}/revoke`,
        {},
      )
    ).data;
  },
  async publicPersonalLanding(token: string): Promise<PublicPersonalLanding> {
    return (
      await httpClient.get<PublicPersonalLanding>(
        `/api/v1/public/landings/${encodeURIComponent(token)}`,
        { authenticated: false, retryOnUnauthorized: false },
      )
    ).data;
  },
  async trackPersonalLanding(
    token: string,
    eventType: string,
    productId?: string,
  ): Promise<PublicPersonalLanding> {
    return (
      await httpClient.post<PublicPersonalLanding>(
        `/api/v1/public/landings/${encodeURIComponent(token)}/events`,
        { eventType, ...(productId ? { productId } : {}) },
        { authenticated: false, retryOnUnauthorized: false },
      )
    ).data;
  },
};
